import { C, Res, type CivDef, type TechDef } from './types';

const MIL = C.Military;
const ECO_TECHS = ['wheelbarrow', 'handCart', 'doubleAxe', 'bowSaw', 'goldMining', 'stoneMining', 'horseCollar', 'heavyPlow'];
const DEFENSIVE = ['watchtower', 'guardTower', 'castle', 'stoneWall', 'stonePillar'];
const CAVALRY_UNITS = ['scout', 'lightCavalry', 'knight', 'cuirassier', 'dragoon', 'horseArcher'];

export const CIVS: CivDef[] = [
  {
    key: 'english', name: 'English', setting: 'Plantagenet and Tudor England, from the Hundred Years War to the industrial revolution.',
    specialisation: ['Long-range archery', 'Defensive warfare', 'Agricultural economy'],
    bonuses: ['Archers +1 range', 'Villagers gather food 10% faster', 'Towers, walls and castles +20% HP'],
    weaknesses: ['Cavalry cost 15% more', 'Siege workshop units train 15% slower'],
    effects: [
      { classes: C.Archer, stat: 'range', value: 1 },
      { classes: C.Villager, res: Res.Food, stat: 'gatherPct', value: 10 },
      { keys: DEFENSIVE, stat: 'buildHpPct', value: 20 },
      { classes: C.Cavalry, stat: 'costPct', value: 15 },
      { classes: C.Siege, stat: 'trainTimePct', value: 15 },
    ],
    uniqueUnits: ['longbowman'], uniqueTechs: ['yeomen'], disabled: [],
    architecture: { wall: 0xd8ccb0, wallDark: 0x6a4a30, roof: 0x5a4636, trim: 0x3b2a1e, roofStyle: 'gable' }, uniform: 0xb02a2a,
  },
  {
    key: 'french', name: 'French', setting: 'Capetian and Valois France through the Bourbon kingdom.',
    specialisation: ['Heavy cavalry', 'Economic development', 'Powerful late-game armies'],
    bonuses: ['Cavalry train 20% faster', 'Cavalry +15% HP', 'Economic upgrades cost 25% less'],
    weaknesses: ['Heavy units cost 10% more'],
    effects: [
      { classes: C.Cavalry, stat: 'trainTimePct', value: -20 },
      { classes: C.Cavalry, stat: 'hpPct', value: 15 },
      { keys: ECO_TECHS, stat: 'costPct', value: -25 },
      { classes: C.Heavy, stat: 'costPct', value: 10 },
    ],
    uniqueUnits: ['royalKnight'], uniqueTechs: ['chivalry'], disabled: [],
    architecture: { wall: 0xe6dcc4, wallDark: 0x8a7a64, roof: 0x3d4e6a, trim: 0x2c3442, roofStyle: 'hip' }, uniform: 0x2a4fb0,
  },
  {
    key: 'germans', name: 'Germans', setting: 'The Holy Roman Empire and the German states to the Prussian era.',
    specialisation: ['Heavy infantry', 'Siege engineering', 'Defensive fortifications'],
    bonuses: ['Siege bonus damage +20%', 'Defensive buildings +25% HP', 'Infantry +1 melee armour'],
    weaknesses: ['Cavalry train 15% slower', 'Military units cost 5% more'],
    effects: [
      { classes: C.Siege, stat: 'bonusDmgPct', value: 20 },
      { keys: DEFENSIVE, stat: 'buildHpPct', value: 25 },
      { classes: C.Infantry, stat: 'meleeArmor', value: 1 },
      { classes: C.Cavalry, stat: 'trainTimePct', value: 15 },
      { classes: MIL, stat: 'costPct', value: 5 },
    ],
    uniqueUnits: ['landsknecht'], uniqueTechs: ['zweihander'], disabled: [],
    architecture: { wall: 0xe8e0cc, wallDark: 0x4a3626, roof: 0x8a3a2a, trim: 0x2a1e16, roofStyle: 'gable' }, uniform: 0x3a3a3a,
  },
  {
    key: 'spanish', name: 'Spanish', setting: 'The Crown of Castile and the Spanish Empire.',
    specialisation: ['Gunpowder', 'Exploration', 'Naval expansion'],
    bonuses: ['Gunpowder units +10 accuracy', 'Scouts +15% speed', 'Trade +15%'],
    weaknesses: ['Imperial and Industrial units cost 10% more'],
    effects: [
      { classes: C.Gunpowder, stat: 'accuracy', value: 10 },
      { classes: C.Scout, stat: 'speedPct', value: 15 },
      { stat: 'tradePct', value: 15 },
      { keys: ['handCannoneer', 'cuirassier', 'bombard', 'lineInfantry', 'rifleman', 'dragoon', 'fieldArtillery', 'heavyArtillery'], stat: 'costPct', value: 10 },
    ],
    uniqueUnits: ['conquistador'], uniqueTechs: ['supremacy'], disabled: [],
    architecture: { wall: 0xf0e4c8, wallDark: 0x9a7448, roof: 0xb4583a, trim: 0x5a3a22, roofStyle: 'hip' }, uniform: 0xd6b02a,
  },
  {
    key: 'portuguese', name: 'Portuguese', setting: 'The Kingdom of Portugal in the Age of Discovery.',
    specialisation: ['Naval trade', 'Exploration', 'Economic flexibility'],
    bonuses: ['Ships cost 20% less', 'Trade +25%', 'Ships +10% speed', 'Start with +100 gold', 'Gold mining +10%'],
    weaknesses: ['Infantry -5% HP'],
    effects: [
      { classes: C.Ship, stat: 'costPct', value: -20 },
      { stat: 'tradePct', value: 25 },
      { classes: C.Ship, stat: 'speedPct', value: 10 },
      { classes: C.Villager, res: Res.Gold, stat: 'gatherPct', value: 10 },
      { classes: C.Infantry, stat: 'hpPct', value: -5 },
    ],
    uniqueUnits: ['caravel'], uniqueTechs: ['carrack'], disabled: [], startBonus: { gold: 100 },
    architecture: { wall: 0xf4ecdc, wallDark: 0x9a8a70, roof: 0xc0603e, trim: 0x2c5a8a, roofStyle: 'hip' }, uniform: 0x2a9a6a,
  },
  {
    key: 'ottomans', name: 'Ottomans', setting: 'The Ottoman Empire from Mehmed II to the Tanzimat reforms.',
    specialisation: ['Artillery', 'Gunpowder infantry', 'Powerful siege warfare'],
    bonuses: ['Artillery costs 25% less', 'Gunpowder units +2 attack', 'Military trains 10% faster'],
    weaknesses: ['Research takes 15% longer'],
    effects: [
      { classes: C.Artillery, stat: 'costPct', value: -25 },
      { classes: C.Gunpowder, stat: 'pierceAttack', value: 2 },
      { classes: MIL, stat: 'trainTimePct', value: -10 },
      { stat: 'researchTimePct', value: 15 },
    ],
    uniqueUnits: ['janissary'], uniqueTechs: ['artillerists'], disabled: [],
    architecture: { wall: 0xf2ead8, wallDark: 0xa08a6a, roof: 0x6a8a9a, trim: 0x3a5a6a, roofStyle: 'dome' }, uniform: 0x8a1a3a,
  },
  {
    key: 'japanese', name: 'Japanese', setting: 'Sengoku-period Japan to the Meiji restoration.',
    specialisation: ['Elite infantry', 'Defensive tactics', 'Efficient resource management'],
    bonuses: ['Infantry attack 20% faster', 'Infantry train 15% faster', 'Spearmen +1 melee armour', 'Wood gathering +10%'],
    weaknesses: ['No Cuirassier', 'Siege costs 20% more'],
    effects: [
      { classes: C.Infantry, stat: 'attackSpeedPct', value: 20 },
      { classes: C.Infantry, stat: 'trainTimePct', value: -15 },
      { classes: C.Spear, stat: 'meleeArmor', value: 1 },
      { classes: C.Villager, res: Res.Wood, stat: 'gatherPct', value: 10 },
      { classes: C.Siege, stat: 'costPct', value: 20 },
    ],
    uniqueUnits: ['samurai'], uniqueTechs: ['bushido'], disabled: ['cuirassier'],
    architecture: { wall: 0xe8e2d4, wallDark: 0x3a2a22, roof: 0x3a3e46, trim: 0x8a2a1a, roofStyle: 'pagoda' }, uniform: 0x7a2ab0,
  },
  {
    key: 'chinese', name: 'Chinese', setting: 'Song and Ming dynasty China to the late Qing self-strengthening movement.',
    specialisation: ['Large armies', 'Economic productivity', 'Technological advancement'],
    bonuses: ['Villagers gather 8% faster', 'Infantry cost 15% less', 'Research 15% faster'],
    weaknesses: ['Infantry and archers -10% HP'],
    effects: [
      { classes: C.Villager, stat: 'gatherPct', value: 8 },
      { classes: C.Infantry, stat: 'costPct', value: -15 },
      { stat: 'researchTimePct', value: -15 },
      { classes: C.Infantry | C.Archer, stat: 'hpPct', value: -10 },
    ],
    uniqueUnits: ['chuKoNu'], uniqueTechs: ['rocketry'], disabled: [],
    architecture: { wall: 0xd8c8a8, wallDark: 0x8a2a1e, roof: 0x2e5a4a, trim: 0xb88a2a, roofStyle: 'pagoda' }, uniform: 0xd07a1a,
  },
  {
    key: 'mongols', name: 'Mongols', setting: 'The Mongol Empire and its successor khanates.',
    specialisation: ['Mobile warfare', 'Cavalry archers', 'Rapid expansion'],
    bonuses: ['Cavalry +10% speed', 'Hunting 40% faster', 'Mounted units train 20% faster', 'Construction 15% faster'],
    weaknesses: ['Towers and walls -15% HP', 'Siege bonus damage -15%'],
    effects: [
      { classes: C.Cavalry, stat: 'speedPct', value: 10 },
      { classes: C.Villager, stat: 'huntPct', value: 40 },
      { stat: 'buildTimePct', value: -15 },
      { classes: C.Cavalry, stat: 'trainTimePct', value: -20 },
      { keys: DEFENSIVE, stat: 'buildHpPct', value: -15 },
      { classes: C.Siege, stat: 'bonusDmgPct', value: -15 },
    ],
    uniqueUnits: ['mangudai'], uniqueTechs: ['nomads'], disabled: [],
    architecture: { wall: 0xe0d4b8, wallDark: 0x7a5a3a, roof: 0xc8b898, trim: 0x3a6a8a, roofStyle: 'dome' }, uniform: 0x2ab0b0,
  },
  {
    key: 'indians', name: 'Vijayanagara', setting: 'The Vijayanagara Empire of southern India (14th to 17th centuries), extended into the Mysore Kingdom for later ages.',
    specialisation: ['Trade', 'Economic development', 'War elephants'],
    bonuses: ['Trade +20%', 'Food gathering +5%', 'Elephants +20% HP'],
    weaknesses: ['Heavy units cost 10% more', 'Heavy units -5% speed'],
    effects: [
      { stat: 'tradePct', value: 20 },
      { classes: C.Villager, res: Res.Food, stat: 'gatherPct', value: 5 },
      { classes: C.Elephant, stat: 'hpPct', value: 20 },
      { classes: C.Heavy, stat: 'costPct', value: 10 },
      { classes: C.Heavy | C.Elephant, stat: 'speedPct', value: -5 },
    ],
    uniqueUnits: ['warElephant'], uniqueTechs: ['mysoreanRockets'], disabled: [],
    architecture: { wall: 0xd8b888, wallDark: 0x8a5a3a, roof: 0xb8783a, trim: 0x6a2a1a, roofStyle: 'dome' }, uniform: 0xe0c040,
  },
  {
    key: 'aztecs', name: 'Aztecs', setting: 'The Mexica Triple Alliance; later ages follow an alternate history of Mesoamerican military innovation.',
    specialisation: ['Infantry warfare', 'Rapid military production', 'Territorial aggression'],
    bonuses: ['Infantry train 20% faster', 'Infantry +1 melee attack'],
    weaknesses: ['No cavalry or stables', 'No European gunpowder or artillery'],
    effects: [
      { classes: C.Infantry, stat: 'trainTimePct', value: -20 },
      { classes: C.Infantry, stat: 'meleeAttack', value: 1 },
    ],
    uniqueUnits: ['jaguarWarrior', 'eagleScout', 'eagleWarrior', 'arrowKnight'], uniqueTechs: ['flowerWars'],
    disabled: [...CAVALRY_UNITS, 'stable', 'handCannoneer', 'bombard', 'lineInfantry', 'rifleman', 'fieldArtillery', 'heavyArtillery', 'foundry', 'arsenal'],
    architecture: { wall: 0xd8ccb0, wallDark: 0x8a6a4a, roof: 0x8a8a6a, trim: 0x9a3a28, roofStyle: 'pyramid' }, uniform: 0x30a030,
  },
  {
    key: 'egyptians', name: 'Egyptians', setting: 'New Kingdom Egypt in early ages; later ages follow the Mamluk Sultanate and the Muhammad Ali modernisation.',
    specialisation: ['Agriculture', 'Defensive construction', 'Economic resilience'],
    bonuses: ['Farming 15% faster', 'Stone buildings cost 20% less', 'Defensive buildings +20% HP'],
    weaknesses: ['Cavalry cost 15% more', 'Gunpowder units train 20% slower'],
    effects: [
      { classes: C.Villager, res: Res.Food, stat: 'gatherPct', value: 15 },
      { keys: ['watchtower', 'guardTower', 'castle', 'stoneWall', 'stonePillar', 'wonder'], stat: 'costPct', value: -20 },
      { keys: DEFENSIVE, stat: 'buildHpPct', value: 20 },
      { classes: C.Cavalry, stat: 'costPct', value: 15 },
      { classes: C.Gunpowder, stat: 'trainTimePct', value: 20 },
    ],
    uniqueUnits: ['royalGuard'], uniqueTechs: ['nileGranaries'], disabled: [],
    architecture: { wall: 0xe8d8a8, wallDark: 0xa8885a, roof: 0xd8c08a, trim: 0xc8963e, roofStyle: 'flat' }, uniform: 0x3a6ad0,
  },
];

/** Civilisation unique technologies, researched at the Castle. */
export const UNIQUE_TECHS: TechDef[] = [
  { key: 'yeomen', civ: 'english', name: 'Yeomen', cost: [300, 0, 250, 0], time: 60, age: 2, researchedAt: 'castle', effects: [{ classes: C.Archer, stat: 'range', value: 1 }, { classes: C.Tower, stat: 'pierceAttack', value: 2 }], description: 'Archers +1 range, towers +2 attack.' },
  { key: 'chivalry', civ: 'french', name: 'Chivalry', cost: [400, 0, 400, 0], time: 60, age: 3, researchedAt: 'castle', effects: [{ classes: C.Cavalry, stat: 'meleeAttack', value: 2 }], description: 'Cavalry +2 attack.' },
  { key: 'zweihander', civ: 'germans', name: 'Zweihänder Drill', cost: [400, 0, 300, 0], time: 60, age: 2, researchedAt: 'castle', effects: [{ classes: C.Infantry, stat: 'meleeAttack', value: 2 }], description: 'Infantry +2 attack.' },
  { key: 'supremacy', civ: 'spanish', name: 'Supremacy', cost: [400, 0, 250, 0], time: 50, age: 2, researchedAt: 'castle', effects: [{ classes: C.Villager, stat: 'meleeAttack', value: 6 }, { classes: C.Villager, stat: 'hp', value: 40 }], description: 'Villagers become capable fighters.' },
  { key: 'carrack', civ: 'portuguese', name: 'Carrack', cost: [0, 300, 250, 0], time: 50, age: 2, researchedAt: 'castle', effects: [{ classes: C.Ship, stat: 'meleeArmor', value: 1 }, { classes: C.Ship, stat: 'pierceArmor', value: 1 }], description: 'Ships +1/+1 armour.' },
  { key: 'artillerists', civ: 'ottomans', name: 'Artillery Corps', cost: [0, 0, 450, 0], time: 60, age: 3, researchedAt: 'castle', effects: [{ classes: C.Artillery, stat: 'range', value: 2 }], description: 'Artillery +2 range.' },
  { key: 'bushido', civ: 'japanese', name: 'Bushido', cost: [400, 0, 300, 0], time: 60, age: 2, researchedAt: 'castle', effects: [{ classes: C.Infantry, stat: 'hpPct', value: 15 }], description: 'Infantry +15% HP.' },
  { key: 'rocketry', civ: 'chinese', name: 'Rocketry', cost: [0, 0, 450, 0], time: 60, age: 2, researchedAt: 'castle', effects: [{ classes: C.Archer, stat: 'pierceAttack', value: 2 }], description: 'Archers +2 attack.' },
  { key: 'nomads', civ: 'mongols', name: 'Steppe Drill', cost: [300, 0, 300, 0], time: 60, age: 2, researchedAt: 'castle', effects: [{ classes: C.MountedArcher, stat: 'attackSpeedPct', value: 20 }], description: 'Cavalry archers attack 20% faster.' },
  { key: 'mysoreanRockets', civ: 'indians', name: 'Mysorean Rockets', cost: [0, 300, 500, 0], time: 70, age: 3, researchedAt: 'castle', effects: [{ classes: C.Siege, stat: 'pierceAttack', value: 6 }, { classes: C.Archer, stat: 'range', value: 1 }], description: 'Siege +6 pierce, archers +1 range.' },
  { key: 'flowerWars', civ: 'aztecs', name: 'Flower Wars', cost: [450, 0, 750, 0], time: 60, age: 3, researchedAt: 'castle', effects: [{ classes: C.Infantry, stat: 'meleeAttack', value: 4 }], description: 'Infantry +4 attack.' },
  { key: 'nileGranaries', civ: 'egyptians', name: 'Nile Granaries', cost: [0, 300, 200, 0], time: 50, age: 2, researchedAt: 'castle', effects: [{ classes: C.Villager, res: Res.Food, stat: 'gatherPct', value: 15 }], description: 'Food gathering +15%.' },
];
